import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Space,
  Typography,
  Drawer,
  Descriptions,
  Form,
  Input,
  Select,
  DatePicker,
  Tag,
  message,
  Spin
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EditOutlined, EyeOutlined, HomeOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import {
  fetchSuperadminTeams,
  patchSuperadminTeam,
  PlatformTeam
} from '../../services/api';
import '../MemberManagement.css';

const { Text } = Typography;

const CATEGORY_LABELS: Record<string, string> = {
  enterprise: '企业/教培机构',
  school: '学校/科研院所',
  research_group: '高校课题组',
  hospital: '医院/医学院',
  government: '政府/事业单位'
};

function displayValue(value?: string | null): string {
  const s = value != null ? String(value).trim() : '';
  return s || '—';
}

function categoryLabel(category?: string | null): string {
  if (!category) return '—';
  return CATEGORY_LABELS[category] || category;
}

const SuperadminTeams: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState<PlatformTeam[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [regDrawerOpen, setRegDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<PlatformTeam | null>(null);
  const [viewingReg, setViewingReg] = useState<PlatformTeam | null>(null);
  const [form] = Form.useForm<{
    name: string;
    status: 'active' | 'suspended';
    planTier: 'free' | 'paid';
    planLabel: string;
    subscriptionEndsAt: Dayjs | null;
  }>();

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetchSuperadminTeams();
      if (res.success && res.data) {
        setTeams(res.data);
      } else {
        message.error(res.error || '加载失败');
      }
    } catch (e) {
      message.error('加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openEdit = (record: PlatformTeam) => {
    setEditing(record);
    form.setFieldsValue({
      name: record.name,
      status: record.status,
      planTier: record.planTier === 'paid' ? 'paid' : 'free',
      planLabel: record.planLabel || '',
      subscriptionEndsAt: record.subscriptionEndsAt ? dayjs(record.subscriptionEndsAt) : null
    });
    setDrawerOpen(true);
  };

  const openRegInfo = (record: PlatformTeam) => {
    setViewingReg(record);
    setRegDrawerOpen(true);
  };

  const handleSave = async () => {
    if (!editing) return;
    try {
      const values = await form.validateFields();
      setSaving(true);
      const ends = values.subscriptionEndsAt;
      const res = await patchSuperadminTeam(editing._id, {
        name: values.name.trim(),
        status: values.status,
        planTier: values.planTier,
        planLabel: values.planLabel?.trim() ?? '',
        subscriptionEndsAt: ends ? ends.endOf('day').toISOString() : null
      });
      if (res.success) {
        message.success('已保存');
        setDrawerOpen(false);
        setEditing(null);
        await load();
      } else {
        message.error(res.error || '保存失败');
      }
    } catch (e) {
      /* validation */
    } finally {
      setSaving(false);
    }
  };

  const subscriptionLabel = (t: PlatformTeam) => {
    if (!t.subscriptionEndsAt) return <Text type="secondary">未设置</Text>;
    const end = new Date(t.subscriptionEndsAt).getTime();
    const expired = end < Date.now();
    return (
      <Space direction="vertical" size={0}>
        <Text>{dayjs(t.subscriptionEndsAt).format('YYYY-MM-DD')}</Text>
        {expired ? <Tag color="red">已到期</Tag> : <Tag color="green">有效</Tag>}
      </Space>
    );
  };

  const columns: ColumnsType<PlatformTeam> = [
    {
      title: '团队编码',
      dataIndex: 'code',
      width: 120,
      fixed: 'left'
    },
    {
      title: '名称',
      dataIndex: 'name',
      ellipsis: true
    },
    {
      title: '管理员邮箱',
      dataIndex: 'adminEmail',
      width: 220,
      ellipsis: true,
      render: (email: string | undefined) => email || '—'
    },
    {
      title: '注册时间',
      dataIndex: 'createdAt',
      width: 170,
      render: (createdAt: string | undefined) =>
        createdAt ? dayjs(createdAt).format('YYYY-MM-DD HH:mm') : '—'
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (s: string) =>
        s === 'active' ? <Tag color="success">正常</Tag> : <Tag color="default">已停用</Tag>
    },
    {
      title: '档位',
      dataIndex: 'planTier',
      width: 88,
      render: (p: string | undefined) =>
        p === 'paid' ? <Tag color="blue">付费</Tag> : <Tag>免费</Tag>
    },
    {
      title: '套餐/备注',
      dataIndex: 'planLabel',
      ellipsis: true,
      render: (v: string) => v || '—'
    },
    {
      title: '订阅到期',
      key: 'sub',
      width: 160,
      render: (_, record) => subscriptionLabel(record)
    },
    {
      title: '成员数',
      dataIndex: 'memberCount',
      width: 88,
      align: 'right'
    },
    {
      title: '单位/类型',
      key: 'regBrief',
      width: 180,
      ellipsis: true,
      render: (_, record) => {
        const org = record.orgName?.trim();
        const cat = categoryLabel(record.category);
        if (!org && cat === '—') return <Text type="secondary">—</Text>;
        return (
          <Space direction="vertical" size={0}>
            <Text ellipsis style={{ maxWidth: 160 }}>
              {org || '—'}
            </Text>
            {cat !== '—' ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {cat}
              </Text>
            ) : null}
          </Space>
        );
      }
    },
    {
      title: '操作',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space size={0}>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => openRegInfo(record)}>
            注册信息
          </Button>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>
            编辑
          </Button>
        </Space>
      )
    }
  ];

  return (
    <div className="page-container full-height members-lims">
      <nav className="mm-crumb" aria-label="breadcrumb">
        <HomeOutlined />
        <span>首页</span>
        <span className="mm-crumb-sep">/</span>
        <span>平台管理</span>
        <span className="mm-crumb-sep">/</span>
        <span>团队管理</span>
      </nav>

      <header className="mm-page-head">
        <div>
          <h1 className="mm-title">团队管理</h1>
          <p className="mm-subtitle mb-0">
            维护团队档位、订阅到期与停用状态；可查看创建团队时提交的注册表单。免费版有成员人数上限；付费档以到期日为准，到期后若人数仍超过免费上限，系统将自动标记团队为停用（全员不可登录）。手动「已停用」也会禁止登录。
          </p>
        </div>
      </header>

      <div className="content-area px-1 px-md-2">
        <div className="am-table-shell bg-white rounded-3 border p-2 p-md-3">
          {loading && teams.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 48 }}>
              <Spin />
            </div>
          ) : (
            <Table
              rowKey="_id"
              columns={columns}
              dataSource={teams}
              loading={loading}
              scroll={{ x: 1500 }}
              pagination={{ pageSize: 20, showSizeChanger: true }}
            />
          )}
        </div>
      </div>

      <Drawer
        title={`注册信息：${viewingReg?.name ?? ''}（${viewingReg?.code ?? ''}）`}
        width={480}
        open={regDrawerOpen}
        onClose={() => {
          setRegDrawerOpen(false);
          setViewingReg(null);
        }}
        destroyOnHidden
      >
        {viewingReg && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                用户创建团队时提交的表单信息
              </Text>
              {viewingReg.createdAt ? (
                <div style={{ marginTop: 4 }}>
                  <Text type="secondary">注册时间：</Text>
                  <Text>{dayjs(viewingReg.createdAt).format('YYYY-MM-DD HH:mm')}</Text>
                </div>
              ) : null}
            </div>
            <Descriptions
              column={1}
              size="small"
              bordered
              labelStyle={{ width: 110, whiteSpace: 'nowrap' }}
            >
              <Descriptions.Item label="团队名称">{displayValue(viewingReg.name)}</Descriptions.Item>
              <Descriptions.Item label="团队编码">{displayValue(viewingReg.code)}</Descriptions.Item>
              <Descriptions.Item label="团队类型">
                {categoryLabel(viewingReg.category)}
              </Descriptions.Item>
              <Descriptions.Item label="单位名称">{displayValue(viewingReg.orgName)}</Descriptions.Item>
              <Descriptions.Item label="团队规模">{displayValue(viewingReg.teamSize)}</Descriptions.Item>
              <Descriptions.Item label="创建人职务">
                {displayValue(viewingReg.ownerPosition)}
              </Descriptions.Item>
              <Descriptions.Item label="所在行业">{displayValue(viewingReg.industry)}</Descriptions.Item>
              <Descriptions.Item label="研究领域">
                {displayValue(viewingReg.researchField)}
              </Descriptions.Item>
              <Descriptions.Item label="团队简介">{displayValue(viewingReg.intro)}</Descriptions.Item>
              <Descriptions.Item label="联系人姓名">
                {displayValue(viewingReg.contactName)}
              </Descriptions.Item>
              <Descriptions.Item label="联系电话">
                {displayValue(viewingReg.contactPhone)}
              </Descriptions.Item>
              <Descriptions.Item label="联系邮箱">
                {displayValue(viewingReg.contactEmail)}
              </Descriptions.Item>
              <Descriptions.Item label="管理员邮箱">
                {displayValue(viewingReg.adminEmail)}
              </Descriptions.Item>
            </Descriptions>
          </Space>
        )}
      </Drawer>

      <Drawer
        title={`编辑团队：${editing?.code ?? ''}`}
        width={400}
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setEditing(null);
        }}
        destroyOnHidden
        extra={
          <Space>
            <Button onClick={() => setDrawerOpen(false)}>取消</Button>
            <Button type="primary" loading={saving} onClick={handleSave}>
              保存
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="团队名称" rules={[{ required: true, message: '请填写名称' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="status" label="状态" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'active', label: '正常' },
                { value: 'suspended', label: '已停用（全员不可登录）' }
              ]}
            />
          </Form.Item>
          <Form.Item name="planTier" label="计费档位" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'free', label: '免费版（有成员人数上限）' },
                { value: 'paid', label: '付费版（以订阅到期日为准）' }
              ]}
            />
          </Form.Item>
          <Form.Item name="planLabel" label="套餐 / 备注">
            <Input placeholder="如：专业版、内部试用" maxLength={80} showCount />
          </Form.Item>
          <Form.Item name="subscriptionEndsAt" label="订阅到期日">
            <DatePicker style={{ width: '100%' }} allowClear format="YYYY-MM-DD" />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
};

export default SuperadminTeams;
